import React from 'react';
import { Badge } from '@razorpay/blade/components';
import { routePaths } from 'components/Routes/data';
import { BulkUploadTypes, UploadTypeConfig, UploadTypeConfigCallback } from 'constants/bulkUpload';
import permissions from 'constants/permissions';
import { BulkUploadStagingDetailsSchemaContract } from 'schemas/BulkUploadStagingDetailsSchema';
import { getPeoplePage } from 'utils/links';

type StagingDataItem = BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging']['data'][0];
type ColumnsArray = NonNullable<UploadTypeConfig['preview']['columns']>;

const getCommonColumns = (): ColumnsArray => [
  {
    title: 'Name',
    render: (item) => item.file_row?.commonProperties?.employeeName,
  },
  {
    title: 'Email Address',
    render: (item) => item.file_row?.commonProperties?.email,
  },
  {
    title: 'Department',
    render: (item) => item.file_row?.department,
  },
  {
    title: 'Designation',
    render: (item) => item.file_row?.designation,
  },
  {
    title: 'CTC',
    render: (item) => item.file_row?.ctc,
  },
];

const getVPFColumns = (): ColumnsArray => [
  ...getCommonColumns(),
  {
    title: 'VPF',
    render: (item) => item.file_row?.vpfAmount,
  },
];

const getAllDeductionKeys = (
  stagingData?: BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging'],
): string[] => {
  const keys = new Set<string>();
  stagingData?.data?.forEach((item) => {
    const deductions = item.file_row?.recurringDeductions;
    if (deductions && typeof deductions === 'object' && !Array.isArray(deductions)) {
      Object.keys(deductions).forEach((k) => keys.add(k));
    }
  });
  return Array.from(keys);
};

const getRecurringDeductionsColumns = (
  stagingData?: BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging'],
): ColumnsArray => {
  const deductionKeys = getAllDeductionKeys(stagingData);

  return [
    ...getCommonColumns(),
    ...deductionKeys.map((deductionName) => ({
      title: deductionName,
      render: (item: StagingDataItem) => item.file_row?.recurringDeductions?.[deductionName],
    })),
  ];
};

const getAllPerquisiteKeys = (
  stagingData?: BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging'],
): string[] => {
  const keys = new Set<string>();
  stagingData?.data?.forEach((item) => {
    const perquisites = item.file_row?.perquisites;
    if (perquisites && typeof perquisites === 'object' && !Array.isArray(perquisites)) {
      Object.keys(perquisites).forEach((k) => keys.add(k));
    }
  });
  return Array.from(keys);
};

const getPerquisitesColumns = (
  stagingData?: BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging'],
): ColumnsArray => {
  const perquisiteKeys = getAllPerquisiteKeys(stagingData);

  const perquisiteColumns: ColumnsArray = [];

  perquisiteKeys.forEach((perquisiteName) => {
    perquisiteColumns.push({
      title: `${perquisiteName} Total Value`,
      render: (item: StagingDataItem) => item.file_row?.perquisites?.[perquisiteName]?.total_value,
    });
    perquisiteColumns.push({
      title: `${perquisiteName} Amount paid by employee`,
      render: (item: StagingDataItem) =>
        item.file_row?.perquisites?.[perquisiteName]?.paid_by_employee,
    });
  });

  return [...getCommonColumns(), ...perquisiteColumns];
};

const getAllFlexiBenefitKeys = (
  stagingData?: BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging'],
): string[] => {
  const keys = new Set<string>();
  stagingData?.data?.forEach((item) => {
    const flexiBenefits = item.file_row?.flexiBenefits;
    if (flexiBenefits && typeof flexiBenefits === 'object' && !Array.isArray(flexiBenefits)) {
      Object.keys(flexiBenefits).forEach((k) => keys.add(k));
    }
  });
  return Array.from(keys);
};

const getFlexiBenefitsColumns = (
  stagingData?: BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging'],
): ColumnsArray => {
  const benefitKeys = getAllFlexiBenefitKeys(stagingData);

  const flexiBenefitsColumns: ColumnsArray = [];

  benefitKeys.forEach((benefitName) => {
    flexiBenefitsColumns.push({
      title: `${benefitName} Status`,
      render: (item: StagingDataItem) => {
        const status = item.file_row?.flexiBenefits?.[benefitName]?.status;
        if (!status) return null;
        const isEnabled = status.toLowerCase() === 'enable' || status.toLowerCase() === 'enabled';
        return (
          <Badge color={isEnabled ? 'positive' : 'negative'}>
            {isEnabled ? 'Enabled' : 'Disabled'}
          </Badge>
        );
      },
    });
    flexiBenefitsColumns.push({
      title: `${benefitName} Amount`,
      render: (item: StagingDataItem) => item.file_row?.flexiBenefits?.[benefitName]?.amount,
    });
  });

  return [...getCommonColumns(), ...flexiBenefitsColumns];
};

export const bulkCompensationAdjustmentConfig: UploadTypeConfigCallback = (data) => {
  const stagingData = data?.stagingRecordData as
    | BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging']
    | undefined;

  return {
    permissions: [permissions.BULK_ADDITION_DEDUCTION],
    initiation: {
      permissions: [permissions.BULK_ADDITION_DEDUCTION],
      feature: 'reArchM3S3',
      pageTitle: 'Bulk Compensation Adjustment',
      step1Title: '1. Download the Compensation Adjustment template',
      step1Subtitle:
        'Download the template and open it in Excel. Fill in the details for recurring deductions, VPF, perquisites, and flexible benefits, then upload it here.',
      instructions: [
        'The template has four tabs: Recurring Deductions, VPF, Perquisites, and Flexible Benefits.',
        'Fill out the relevant tabs as needed.',
        'Do not add or remove any columns.',
        'There is a limit of 500 employees per upload.',
        "If a field is empty, leave it blank (don't enter '-', 'NA,' etc.).",
        'Mandatory fields are mentioned with * sign.',
      ],
      onSampleTemplateDownload() {
        window.open(`/v2/api/bulk-flexi-perq-deductions-vpf/download-template`, '_blank');
      },
    },
    preview: {
      permissions: [permissions.BULK_ADDITION_DEDUCTION],
      feature: 'reArchM3S3',
      showColumnsSelector: false,
      sheets: {
        'Recurring deductions': {
          columns: getRecurringDeductionsColumns(stagingData),
        },
        VPF: {
          columns: getVPFColumns(),
        },
        Perquisites: {
          columns: getPerquisitesColumns(stagingData),
        },
        'Flexible benefits': {
          columns: getFlexiBenefitsColumns(stagingData),
        },
      },
      renderConfirmationText: (uploadData) =>
        `Almost done! Please confirm to proceed with the bulk non-salary adjustments process.`,
      successModalCtaText: 'Go to People',
      successModalMainText: 'You have successfully adjusted compensation for your employees.',
      successModalSubText: () =>
        'Need to make changes? Edit employee info anytime from the “People” page.',
      onSuccessModalCtaClick() {
        const peoplePageUrl = getPeoplePage(data.featureFlags?.employeeOnboarding);
        data.history.push(peoplePageUrl);
      },
      onDownloadErrorReport(history) {
        history?.push({
          pathname: routePaths.bulkUploads.initiate,
          search: `type=${BulkUploadTypes.TYPE_BULK_COMPENSATION_ADJUSTMENT}`,
        });
      },
    },
  };
};
